import {
  CollectionsRowWrapper,
  DesktopFiltersContainer,
  FacetsContainer,
  FilterButton,
  FiltersWrapper,
  SortStatsWrapper,
  StatsWrapper,
  ResetAllFilters,
} from "./Facets.styled";
import { Filters } from "../Filters";
import { Sort } from "../Sort/";
import { Filtering, Refresh } from "src/icons";
import { Trans } from "@lingui/macro";
import { Text } from "@otrium/atoms";
import NoSSR from "src/atoms/NoSSR";
import { useBreakpoints } from "@otrium/core";
import { MutableRefObject, useCallback, useMemo, useState } from "react";
import { SEGMENT_LIST_FORMAT } from "src/segment";
import { localeFix } from "src/utils/genderLocale/genderLocale";
import { FacetProvider, useFacetContext } from "./facet.context";
import { scrollToElement } from "src/utils/scrollToElement";
import { HEADER_STICKY_OFFSET } from "src/utils/constants";
import { useRemoveFilters } from "src/hooks/use-handle-search-params";
import type { Gender, ProductListType } from "src/types/product-listing";
import {
  PRODUCT_LIST_CONTEXT_KEYS,
  PRODUCT_LISTING_TYPE,
} from "src/utils/product-list-context-keys";
import { useCategoryTreeDispatch } from "src/hooks/context/use-category-tree-context";
import type { ListingFacetKeys } from "src/types/adapters/listing.type";
import { useListingState } from "src/hooks/stores/use-listing-store";
import { Portal } from "src/atoms/Portal";
import {
  EXCLUDED_MOBILE_FACETS,
  MobileFilters,
} from "src/organisms/MobileFilters";

export type FacetsProps = {
  selectedFacets: readonly ListingFacetKeys[];
  categorySlug?: string;
  gender?: Gender;
  pageContentType: ProductListType;
  title?: string;
  formattedTitle?: boolean;
  gridRef: MutableRefObject<HTMLDivElement | null>;
};

const INLINE_FILTERS_PLP_TYPES: ProductListType[] = ["COLLECTIONS_PLP"];

function FacetsLayout() {
  const { isDesktop } = useBreakpoints();
  const [isMobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const { appliedFacets } = useListingState();
  const { removeAllFilters } = useRemoveFilters();
  const treeDispatch = useCategoryTreeDispatch();
  const { trackFilterReset, handleScrollToTop, pageContentType } =
    useFacetContext();

  const totalAppliedFilters = useMemo(
    () => Object.keys(appliedFacets).length,
    [appliedFacets]
  );

  const totalFilters = useMemo(
    () =>
      Object.keys(appliedFacets).filter(
        (key) => !EXCLUDED_MOBILE_FACETS.includes(key as ListingFacetKeys)
      ).length,
    [appliedFacets]
  );

  const toggleMobileFilters = useCallback(
    () => setMobileFiltersOpen((prevState) => !prevState),
    [setMobileFiltersOpen]
  );

  const handleResetAllFilters = useCallback(() => {
    removeAllFilters(true);
    treeDispatch({ type: "TRIGGER_RESET", payload: undefined });
    trackFilterReset();
    handleScrollToTop();
  }, [removeAllFilters, treeDispatch, trackFilterReset, handleScrollToTop]);

  return (
    <NoSSR>
      {isDesktop && INLINE_FILTERS_PLP_TYPES.includes(pageContentType) ? (
        <CollectionsRowWrapper>
          <FacetsContainer>
            <DesktopFiltersContainer>
              <FiltersWrapper>
                <Filters />
              </FiltersWrapper>
            </DesktopFiltersContainer>
          </FacetsContainer>

          <SortStatsWrapper>
            {totalAppliedFilters > 0 && (
              <ResetAllFilters onClick={handleResetAllFilters}>
                <Refresh color="#000" width={12} height={12} />
                <span>
                  <Trans>Reset all</Trans>
                </span>
              </ResetAllFilters>
            )}

            <Sort handleScrollToTop={handleScrollToTop} />
          </SortStatsWrapper>
        </CollectionsRowWrapper>
      ) : (
        <FacetsContainer>
          {isDesktop ? (
            <DesktopFiltersContainer>
              <FiltersWrapper>
                <Filters />
              </FiltersWrapper>
            </DesktopFiltersContainer>
          ) : (
            <StatsWrapper>
              <FilterButton onClick={toggleMobileFilters}>
                <Filtering />
                <Text display="flex" fontWeight={600} ml={2} color={"Black"}>
                  <Trans>Filters</Trans>
                  {totalFilters > 0 ? (
                    <Text ml={1}>{`(${totalFilters})`}</Text>
                  ) : null}
                </Text>
              </FilterButton>
              <SortStatsWrapper>
                <Sort handleScrollToTop={handleScrollToTop} />
              </SortStatsWrapper>
            </StatsWrapper>
          )}
        </FacetsContainer>
      )}

      {!isDesktop && (
        <Portal selector="#portal">
          <MobileFilters
            isOpen={isMobileFiltersOpen}
            toggleMobileFilters={toggleMobileFilters}
          />
        </Portal>
      )}
    </NoSSR>
  );
}

function Facets({
  selectedFacets = [],
  gender,
  pageContentType,
  categorySlug,
  title,
  gridRef,
}: FacetsProps): JSX.Element {
  const handleScrollToTop = useCallback(() => {
    scrollToElement(gridRef.current, HEADER_STICKY_OFFSET, pageContentType);
  }, [gridRef, pageContentType]);

  return (
    <FacetProvider
      handleScrollToTop={handleScrollToTop}
      listFormat={SEGMENT_LIST_FORMAT.default}
      listId={title || PRODUCT_LISTING_TYPE[pageContentType]}
      listType={PRODUCT_LIST_CONTEXT_KEYS[pageContentType]}
      shopType={localeFix({ gender })}
      selectedFacets={selectedFacets}
      pageContentType={pageContentType}
      gender={gender}
      categorySlug={categorySlug}
      gridRef={gridRef}
    >
      <FacetsLayout />
    </FacetProvider>
  );
}

export { Facets };
